/*
  SHARR mini LP -- sticky CTA behaviour, CTA image layer animation, and
  Meta Pixel click tracking.

  Mobile only (<=749px). Hidden while the page's FV section is still on
  screen; shown once the visitor scrolls past the FV section's bottom
  edge; hidden again if they scroll back up to it. Always hidden at
  >=750px (desktop) -- the CSS media query in sharr-mini-lp.css enforces
  that at the same 750px breakpoint, and this script never overrides it.

  Watches the FV section itself (marked with the [data-sharr-mini-fv]
  attribute, output by sharr-mini-image-section.liquid when its "この
  セクションをFVとして使用する" setting is on), not the small transparent
  LINE hotspot drawn inside the FV image -- the whole section is a much
  bigger, more reliable target and this logic is completely independent
  of wherever the hotspot happens to sit.

  A "Sticky CTAを常時表示してテスト" checkbox on the section forces the
  bar to display regardless of scroll position (sharr-mini-sticky-cta--
  debug-always-show class, handled below) -- a temporary tool for telling
  apart a CSS/markup problem from a scroll-logic problem.

  Robustness notes:
  - DOM references are re-queried on every call, never cached, so this
    keeps working after Shopify's theme editor swaps a section's markup
    via shopify:section:load / shopify:section:unload / reorder, with no
    special re-init step required.
  - Initialisation does not rely on a single event: it runs immediately
    if the DOM is already parsed (relevant because this script tag has
    `defer`, so by the time it executes the DOM is normally already
    ready), again on DOMContentLoaded / load as a fallback for any timing
    edge case, and twice more on a short delay to correct for the FV
    section's height changing after first paint (a web font or a lazy
    image finishing loading can shift the whole page's layout).
  - A boot guard on `window` prevents this file from double-initialising
    (adding duplicate listeners) if it is ever accidentally included on a
    page more than once.

  Meta Pixel click tracking (see also bindPixelClickTracking() below):
  - Purely data-driven. Any <a data-pixel-event="EventName" href="...">
    element -- currently the FV/schedule image hotspots and the sticky CTA,
    output by sharr-mini-image-section.liquid / sharr-mini-sticky-cta.liquid
    only when their "pixel_event_name" setting is filled in -- fires
    fbq('trackCustom', 'EventName') exactly once on click, then continues on
    to the link's normal href. Elements with no data-pixel-event attribute
    (the default, when the setting is left blank) are completely untouched.
  - A single delegated listener on `document` covers every current and
    future data-pixel-event element, including ones Shopify's theme editor
    swaps in later via shopify:section:load -- no per-section re-binding.
  - fbq is only ever called if it already exists as a function
    (typeof fbq === 'function'); this file never loads or configures Meta
    Pixel itself, so it can never create a second/duplicate Pixel install.
  - Only a plain primary-button left click is intercepted; a modified click
    (ctrl/cmd/shift/alt) or a non-primary button is left alone so opening
    the link in a new tab, copying it, etc. still works exactly as a normal
    link would.
  - Navigation happens via a short (120ms) delayed redirect after firing
    the pixel event, which is the standard safe pattern for "track then
    leave the page" links: it gives the tracking request time to be sent
    before the browser navigates away, without being long enough for a
    visitor to notice any delay.

  CTA image layer animation (see playCtaAnimation() / checkCtaViewTriggers()
  further down): a separate, independent feature added alongside the above,
  not a replacement for any of it. It only ever touches elements carrying
  the sharr-mini-cta-layer class, which sharr-mini-image-section.liquid only
  outputs when a merchant explicitly turns on "CTA画像を使用する" for that
  section instance -- so on every existing image section that hasn't opted
  in, none of this code finds anything to do. Its <a> element is a normal
  data-pixel-event link like any other, so Meta Pixel tracking for it is
  already fully covered by bindPixelClickTracking() above with no extra
  code.

  Two-button CTA row (sharr-mini-price.liquid's "2つのボタンを並べて表示
  する" -- e.g. a "買いたい" / "悩み中" pair): reuses this exact same
  animation system via a second class, sharr-mini-cta-anim (identical
  data-cta-action / data-cta-animate-view / data-cta-animate-hover
  handling, see checkCtaViewTriggers() and the mouseenter listener below --
  both now match either class). Only rendered when a merchant fills in
  both buttons' image + link settings.

  Click-to-Google-Sheet logging (see bindSheetClickLogging() further down):
  independent of Meta Pixel tracking and of the animation above. Any
  element carrying both data-sheet-webhook="<Apps Script Web App URL>" and
  data-sheet-label="<free text>" gets one navigator.sendBeacon() (or a
  fetch(..., {keepalive:true}) fallback) fired to that URL on click, purely
  fire-and-forget -- it never calls preventDefault(), so it can never delay
  or block the visitor's navigation, and a failed/blocked beacon silently
  does nothing rather than breaking the click.

  CTA popup mode (see bindCtaPopups() / buildCtaTrackingUrl() /
  sendCtaTrackingRequest() further down): an alternative to "click the CTA
  -> navigate to cta_link_url" for the 前日告知 (pre-announcement)
  "買いたい"/"悩み中" pair. sharr-mini-image-section.liquid /
  sharr-mini-sticky-cta.liquid output data-cta-popup="buy" or
  data-cta-popup="consider" on a CTA's <a> only when its section's "CTA
  クリックでポップアップを表示する" setting is on.

  IMPORTANT -- "CTAリンク先URL" and Google Apps Script tracking are two
  completely independent things while popup mode is on. The CTA's own
  href/cta_link_url is whatever real destination page the merchant chooses
  (or blank, since there is none yet) -- it is never navigated to while
  popup mode is on, but it is also never read for tracking purposes. The
  Google Apps Script Web App URL used for tracking instead comes from its
  OWN dedicated setting ("Google Apps Script WebアプリURL", output as each
  CTA's data-gas-url attribute) so the two can never be confused with each
  other or accidentally tied together again.

  Clicking such a CTA does all of the following, in this order: (1)
  e.preventDefault() stops navigation only -- see the click handler in
  bindCtaPopups(); (2) the matching popup image is shown immediately,
  synchronously, with no dependency on step (3) succeeding, failing, or
  even finishing; (3) buildCtaTrackingUrl() reads that element's
  data-gas-url plus its data-customer-type/data-discount/data-choice/
  data-page-type attributes, translates the first/third/fourth to Japanese
  via the same SHEET_*_JA lookup tables bindSheetClickLogging() uses, and
  builds "<data-gas-url>?customer_type=...&discount=...&choice=...&
  page_type=..."; sendCtaTrackingRequest() then fires exactly one
  background GET to that URL (see its own doc comment for why fetch+
  no-cors+keepalive was chosen over sendBeacon/a plain fetch), which never
  blocks/delays the popup and never navigates the browser -- the visitor
  never sees Google's own "OK"/JSON response; (4) Meta Pixel fires exactly
  as any other data-pixel-event element does (bindPixelClickTracking),
  which also skips its normal delayed window.location redirect for any
  data-cta-popup element regardless of whether href happens to be blank or
  filled in. The OLD JSON-POST Google Sheet logger (bindSheetClickLogging,
  driven by data-sheet-webhook/data-sheet-label) is deliberately NOT
  output on a data-cta-popup element (see the `unless cta_popup_enabled`
  guard in the .liquid files) so that exactly one Apps Script request
  fires per click, never two. Every existing data-pixel-event /
  data-sheet-webhook element that does NOT carry data-cta-popup (hotspots,
  the teaser FV CTA, the single-button sticky CTA, the price-block CTA
  row) is completely unaffected -- popup mode is off by default on every
  section.
*/
(function () {
  if (window.__sharrMiniStickyBooted) return;
  window.__sharrMiniStickyBooted = true;

  var MOBILE_MAX_WIDTH = 749;

  function updateSticky() {
    var sticky = document.querySelector('[data-sharr-mini-sticky]');
    if (!sticky) return;

    var isMobile = window.innerWidth <= MOBILE_MAX_WIDTH;

    // Debug override: force-show regardless of scroll/FV state, mobile
    // widths only (desktop stays hidden via the CSS media query no matter
    // what class is present).
    if (sticky.classList.contains('sharr-mini-sticky-cta--debug-always-show')) {
      sticky.classList.toggle('is-visible', isMobile);
      document.body.classList.toggle('sharr-mini-has-sticky-cta', isMobile);
      return;
    }

    var fv = document.querySelector('[data-sharr-mini-fv]');
    var shouldShow = false;
    if (fv && isMobile) {
      shouldShow = fv.getBoundingClientRect().bottom <= 0;
    }

    sticky.classList.toggle('is-visible', shouldShow);
    document.body.classList.toggle('sharr-mini-has-sticky-cta', shouldShow);
  }

  window.addEventListener('scroll', updateSticky, { passive: true });
  window.addEventListener('resize', updateSticky);

  // Theme editor: a section can be swapped/re-rendered via AJAX without a
  // full page reload (editing settings, adding/removing/reordering
  // sections). Re-run the check right away whenever that happens.
  document.addEventListener('shopify:section:load', updateSticky);
  document.addEventListener('shopify:section:unload', updateSticky);
  document.addEventListener('shopify:section:reorder', updateSticky);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateSticky);
  } else {
    updateSticky();
  }
  window.addEventListener('load', updateSticky);
  // Safety net for layout shifts shortly after first paint (web fonts,
  // lazy-loaded images changing the FV section's measured height).
  setTimeout(updateSticky, 500);
  setTimeout(updateSticky, 1500);

  /*
    CTA image layer animation (independent of everything above -- does not
    read or modify the sticky CTA's own state at all).

    Elements: <a class="sharr-mini-cta-layer" data-cta-action="...">,
    output only when a merchant turns the feature on for a given image
    section (see sharr-mini-image-section.liquid). Two independently
    toggleable triggers, matching the "表示時" / "ホバー時" settings:

    - data-cta-animate-view="1": play the selected animation exactly once,
      the first time the element scrolls into the viewport. Uses the same
      plain scroll/resize + getBoundingClientRect() approach as
      updateSticky() above (no IntersectionObserver), and a per-element
      "done" flag on the element's own dataset so it can never replay on
      later scrolls -- this is deliberately a one-shot effect, not a
      continuously-looping one.
    - data-cta-animate-hover="1": play the selected animation again on
      every mouseenter (desktop hover). mouseenter does not bubble, so this
      uses a single capture-phase listener on `document` (the standard way
      to delegate a non-bubbling event) rather than one listener per CTA
      instance -- this also automatically covers any CTA layer element the
      theme editor swaps in later via shopify:section:load, with no
      re-binding step.

    Which animation plays (bounce/pulse/grow/float/none) is picked purely
    by CSS via the [data-cta-action="..."] attribute selectors in
    sharr-mini-lp.css; this file only ever adds/removes one class
    (.sharr-mini-cta-anim-play) to start/replay whichever keyframe rule
    already matches that attribute. `prefers-reduced-motion: reduce` is
    handled entirely in CSS (that class becomes a no-op there), so no
    extra check is needed here.
  */
  function playCtaAnimation(el) {
    // Re-triggering a CSS animation requires actually removing the class,
    // forcing a reflow, then re-adding it -- simply toggling/re-adding an
    // already-present class is a no-op and would not restart the keyframe
    // animation (same principle as classList.toggle() being a no-op for
    // the sticky CTA's .is-visible above, just deliberately worked around
    // here because a CTA hover is meant to replay every time).
    el.classList.remove('sharr-mini-cta-anim-play');
    void el.offsetWidth; // eslint-disable-line no-unused-expressions
    el.classList.add('sharr-mini-cta-anim-play');
  }

  function checkCtaViewTriggers() {
    var els = document.querySelectorAll('[data-cta-animate-view="1"]');
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.dataset.ctaViewDone === '1') continue;
      var rect = el.getBoundingClientRect();
      var inView = rect.bottom > 0 && rect.top < viewportHeight;
      if (inView) {
        el.dataset.ctaViewDone = '1';
        playCtaAnimation(el);
      }
    }
  }

  window.addEventListener('scroll', checkCtaViewTriggers, { passive: true });
  window.addEventListener('resize', checkCtaViewTriggers);
  document.addEventListener('shopify:section:load', checkCtaViewTriggers);
  document.addEventListener('shopify:section:reorder', checkCtaViewTriggers);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkCtaViewTriggers);
  } else {
    checkCtaViewTriggers();
  }
  window.addEventListener('load', checkCtaViewTriggers);
  setTimeout(checkCtaViewTriggers, 500);
  setTimeout(checkCtaViewTriggers, 1500);

  // Hover trigger: capture-phase delegation on document, since mouseenter
  // does not bubble. Only reacts to the CTA layer element itself (not its
  // inner <img>, which has pointer-events:none in CSS anyway) so this
  // fires exactly once per actual hover-enter, not twice.
  document.addEventListener('mouseenter', function (e) {
    var el = e.target;
    if (
      el &&
      el.nodeType === 1 &&
      el.classList &&
      (el.classList.contains('sharr-mini-cta-layer') || el.classList.contains('sharr-mini-cta-anim')) &&
      el.getAttribute('data-cta-animate-hover') === '1'
    ) {
      playCtaAnimation(el);
    }
  }, true);

  function bindPixelClickTracking() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('[data-pixel-event]') : null;
      if (!link) return;

      var eventName = link.getAttribute('data-pixel-event');
      var href = link.getAttribute('href');
      var isPopupCta = link.hasAttribute('data-cta-popup');
      // A popup-mode CTA never navigates anywhere (see bindCtaPopups()), so
      // its href/cta_link_url is free to be genuinely blank -- Pixel must
      // still fire in that case. Every other data-pixel-event element
      // (hotspots, the teaser FV CTA, the single-button sticky CTA) really
      // does navigate on click, so href is still required there exactly as
      // before.
      if (!eventName) return;
      if (!isPopupCta && !href) return;

      // Let modified clicks / non-primary-button clicks behave exactly like
      // a normal link (new tab, copy link, etc.) -- only a plain left click
      // is tracked-then-redirected.
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }

      e.preventDefault();

      try {
        if (typeof fbq === 'function') {
          // Optional event parameters -- purely additive: only the elements
          // that carry these data attributes (currently the "買いたい" /
          // "悩み中" CTA pair) send a parameters object at all; every other
          // existing data-pixel-event link (hotspots, the teaser FV CTA,
          // the single-button sticky CTA) has none of these attributes, so
          // it keeps calling fbq('trackCustom', eventName) with no third
          // argument exactly as before -- byte-for-byte unchanged behaviour.
          var pixelParams = {};
          var customerType = link.getAttribute('data-customer-type');
          var discount = link.getAttribute('data-discount');
          var pageType = link.getAttribute('data-page-type');
          if (customerType) pixelParams.customer_type = customerType;
          if (discount) pixelParams.discount = discount;
          if (pageType) pixelParams.page_type = pageType;

          if (Object.keys(pixelParams).length > 0) {
            fbq('trackCustom', eventName, pixelParams);
          } else {
            fbq('trackCustom', eventName);
          }
        }
      } catch (err) {
        // Never let a Pixel/analytics failure block the visitor from
        // reaching the LINE link.
      }

      // CTA popup mode: bindCtaPopups() is what shows the popup AND fires
      // the background Google Apps Script tracking request (built from this
      // element's OWN data-gas-url attribute, completely independent of
      // href/cta_link_url -- see the file-level comment) -- so this
      // function's job stops here for that element; it already fired the
      // Pixel event above, and href may be blank or point to some future
      // real destination page, neither of which this function should ever
      // act on while popup mode is on.
      if (isPopupCta) return;

      var target = link.getAttribute('target');
      setTimeout(function () {
        if (target === '_blank') {
          window.open(href, '_blank', 'noopener');
        } else {
          window.location.href = href;
        }
      }, 120);
    });
  }

  /*
    Click-to-Google-Sheet logging: purely additive, independent of Meta
    Pixel tracking above (a click can carry both data-pixel-event and
    data-sheet-webhook/data-sheet-label, neither one affects the other).
    Never calls preventDefault() -- sendBeacon() is specifically designed
    to survive the page navigating away immediately afterwards, so there is
    no need to delay the click the way bindPixelClickTracking() does. A
    failed/blocked beacon (network error, ad blocker, etc.) is silently
    swallowed by the try/catch below -- it can never stop the click from
    reaching its normal href.

    Payload fields: timestamp/customer_type/discount/choice/page/url_params
    are the 6 columns the 前日告知 (pre-announcement) "買いたい"/"悩み中"
    CTAs need (see CTAClickLogger.gs); label/event/href/referrer/userAgent
    are kept alongside them as extra, human-readable context columns for
    any other element that only sets data-sheet-webhook/data-sheet-label
    (e.g. the price-block CTA row) without the newer data-customer-type/
    data-discount/data-choice attributes -- those simply come through as
    empty strings for that element, exactly as before this feature existed.

    Japanese display values: the spreadsheet is read by non-technical staff,
    so customer_type/choice/page are translated from their raw internal
    values (used as-is everywhere else -- Meta Pixel params, data attribute
    values, section settings) into plain Japanese via the lookup tables
    below, purely for this one outgoing payload. Discount is sent as-is
    (already a plain number like "10"/"20", nothing to translate). An
    unrecognised or missing raw value falls back to itself unchanged, so
    this can never turn a value into "undefined" or blank, and any future
    customer_type/page_type this lookup doesn't yet know about still gets
    logged (untranslated) instead of disappearing.
  */
  var SHEET_CUSTOMER_TYPE_JA = { new: '新規', repeat: 'ご愛用者' };
  var SHEET_CHOICE_JA = { buy: '買いたい', consider: '悩み中' };
  var SHEET_PAGE_TYPE_JA = { 'presale-new': '新規', 'presale-repeat': 'ご愛用者' };

  function translateForSheet(map, rawValue, fallback) {
    if (!rawValue) return fallback || '';
    return map[rawValue] || rawValue;
  }

  function bindSheetClickLogging() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('[data-sheet-webhook][data-sheet-label]') : null;
      if (!link) return;

      var webhook = link.getAttribute('data-sheet-webhook');
      var label = link.getAttribute('data-sheet-label');
      if (!webhook || !label) return;

      try {
        var rawCustomerType = link.getAttribute('data-customer-type') || '';
        var rawChoice = link.getAttribute('data-choice') || '';
        var rawPageType = link.getAttribute('data-page-type') || '';

        var payload = JSON.stringify({
          timestamp: new Date().toISOString(),
          customer_type: translateForSheet(SHEET_CUSTOMER_TYPE_JA, rawCustomerType, ''),
          discount: link.getAttribute('data-discount') || '',
          choice: translateForSheet(SHEET_CHOICE_JA, rawChoice, label),
          // "page" used to be the current page's full URL; now that every
          // caller of this function also sends data-page-type, it instead
          // holds that page's plain-Japanese category (新規 / ご愛用者),
          // which is what the 前日告知 spreadsheet needs in this column.
          // Falls back to the old URL behaviour for any element that has
          // no data-page-type at all, so nothing regresses for it.
          page: rawPageType ? translateForSheet(SHEET_PAGE_TYPE_JA, rawPageType, rawPageType) : window.location.href,
          url_params: window.location.search || '',
          label: label,
          event: link.getAttribute('data-pixel-event') || '',
          href: link.getAttribute('href') || '',
          referrer: document.referrer || '',
          userAgent: navigator.userAgent
        });

        if (navigator.sendBeacon) {
          navigator.sendBeacon(webhook, payload);
        } else {
          fetch(webhook, { method: 'POST', mode: 'no-cors', keepalive: true, body: payload });
        }
      } catch (err) {
        // Never let a logging failure affect the visitor's click.
      }
    });
  }

  /*
    Builds the Google Apps Script tracking URL for a popup-mode CTA click
    (see the file-level comment above): the merchant's own "Google Apps
    Script WebアプリURL" setting (output as this element's data-gas-url
    attribute -- completely separate from href/cta_link_url) plus a query
    string built from this same element's data-customer-type/data-discount/
    data-choice/data-page-type attributes. customer_type/choice/page_type
    are translated to Japanese via the exact same SHEET_*_JA lookup tables
    bindSheetClickLogging() uses above, so the two mechanisms always agree
    on what "新規"/"ご愛用者"/"買いたい"/"悩み中" mean -- discount is sent
    as-is (already a plain number like "10"/"20"). Returns '' (meaning
    "don't send anything") when data-gas-url is blank, e.g. because the
    merchant hasn't filled in that setting yet.
  */
  function buildCtaTrackingUrl(el) {
    var baseUrl = el.getAttribute('data-gas-url');
    if (!baseUrl) return '';

    var rawCustomerType = el.getAttribute('data-customer-type') || '';
    var rawDiscount = el.getAttribute('data-discount') || '';
    var rawChoice = el.getAttribute('data-choice') || '';
    var rawPageType = el.getAttribute('data-page-type') || '';

    var params = {
      customer_type: translateForSheet(SHEET_CUSTOMER_TYPE_JA, rawCustomerType, rawCustomerType),
      discount: rawDiscount,
      choice: translateForSheet(SHEET_CHOICE_JA, rawChoice, rawChoice),
      page_type: translateForSheet(SHEET_PAGE_TYPE_JA, rawPageType, rawPageType)
    };

    var query = [];
    for (var key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        query.push(encodeURIComponent(key) + '=' + encodeURIComponent(params[key]));
      }
    }

    return baseUrl + (baseUrl.indexOf('?') === -1 ? '?' : '&') + query.join('&');
  }

  /*
    Fires the background GET built by buildCtaTrackingUrl() above, once,
    without ever navigating the browser there and without ever blocking or
    delaying the popup that's already on screen by the time this runs.

    Deliberately NOT navigator.sendBeacon(): sendBeacon always sends a
    POST, and this Apps Script reads its parameters via doGet(e), so a
    beacon would hit the wrong handler and record nothing. Deliberately
    NOT a plain `await fetch(url)` either: the response is cross-origin
    and this page never needs to read it (nor does the user need to see
    the "OK" text doGet() returns), so requiring a readable response would
    only invite CORS failures for no benefit. `fetch(..., {mode:'no-cors',
    keepalive:true})` is the primary method -- keepalive lets the request
    finish even if the tab were to unload right after, and no-cors means
    we never try (and never need) to read the opaque response. Where
    fetch() itself doesn't exist, a classic tracking-pixel `new Image()`
    GET is used instead, which works cross-origin with no CORS involved at
    all. Either way, a failure here is logged with console.warn and never
    allowed to affect the popup or the click itself.
  */
  function sendCtaTrackingRequest(url) {
    if (!url || url === '#' || url.indexOf('javascript:') === 0) return;
    try {
      if (window.fetch) {
        fetch(url, { method: 'GET', mode: 'no-cors', keepalive: true }).catch(function (err) {
          if (window.console && console.warn) {
            console.warn('[SHARR mini] CTA tracking request failed', err);
          }
        });
      } else {
        var img = new Image();
        img.src = url;
      }
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('[SHARR mini] CTA tracking request threw', err);
      }
    }
  }

  /*
    CTA popup mode -- see the file-level comment above for the full picture.
    This function only ever shows/hides the popup markup that
    sharr-mini-image-section.liquid outputs once per page (guarded by its
    own "CTAクリックでポップアップを表示する" setting); if that markup isn't
    present on a given page (feature not in use there), the very first
    querySelector below returns null and every click is silently ignored by
    this function -- everything else on the page keeps working normally.
  */
  function bindCtaPopups() {
    var overlay = document.querySelector('[data-cta-popup-overlay]');
    if (!overlay) return;

    function allPanels() {
      return overlay.querySelectorAll('[data-cta-popup-panel]');
    }

    function closePopup() {
      overlay.setAttribute('hidden', '');
      var panels = allPanels();
      for (var i = 0; i < panels.length; i++) panels[i].setAttribute('hidden', '');
      document.body.classList.remove('sharr-mini-popup-open');
      document.removeEventListener('keydown', onKeydown);
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') closePopup();
    }

    function openPopup(key) {
      var panel = overlay.querySelector('[data-cta-popup-panel="' + key + '"]');
      if (!panel) return;
      var panels = allPanels();
      for (var i = 0; i < panels.length; i++) panels[i].setAttribute('hidden', '');
      panel.removeAttribute('hidden');
      overlay.removeAttribute('hidden');
      document.body.classList.add('sharr-mini-popup-open');
      document.addEventListener('keydown', onKeydown);
    }

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest ? e.target.closest('[data-cta-popup]') : null;
      if (trigger) {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        openPopup(trigger.getAttribute('data-cta-popup'));
        // Popup shows immediately above; the background tracking request
        // is fire-and-forget and never awaited/blocking, so a slow or
        // failed Google response can never delay or hide the popup. The
        // tracking URL is built from this element's OWN data-gas-url
        // setting, completely independent of its href/cta_link_url (see
        // buildCtaTrackingUrl()'s doc comment and the file-level comment).
        sendCtaTrackingRequest(buildCtaTrackingUrl(trigger));
        return;
      }

      // Tap/click on the dimmed backdrop itself (not on the panel or
      // anything inside it, e.g. the LINE hotspot) closes the popup.
      if (e.target === overlay) {
        closePopup();
        return;
      }

      var closeBtn = e.target.closest ? e.target.closest('[data-cta-popup-close]') : null;
      if (closeBtn) {
        e.preventDefault();
        closePopup();
      }
    });
  }

  bindPixelClickTracking();
  bindSheetClickLogging();
  bindCtaPopups();
})();
